import { DEFAULT_LABELS } from '@/utils/cad-sheet';
import { createCadSheet, listCadSheets, getCadSheet, updateCadSheet, deleteCadSheet, bulkDeleteCadSheets } from '@/utils/api';

/** Convert a blob URL to a base64 data URL */
async function blobUrlToBase64(url) {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  // Skip non-blob URLs (http URLs from server)
  if (url.startsWith('http')) return url;
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}

/** Prepare CADSheetData for storage */
export async function serializeSheet(data) {
  const images = await Promise.all(
    data.images.map(async (img) => ({
      label: img.label,
      base64: await blobUrlToBase64(img.url),
      annotations: img.annotations.map(({ x1, y1, x2, y2, value, textOffsetX, textOffsetY, style, dimensionGeometry }) => ({ x1, y1, x2, y2, value, textOffsetX, textOffsetY, style, dimensionGeometry })),
      textBoxes: (img.textBoxes || []).map(({ x, y, value, style }) => ({ x, y, value, style })),
      highlights: (img.highlights || []).map(({ points, color, opacity, strokeWidth }) => ({ points, color, opacity, strokeWidth })),
    }))
  );

  const customerImagesBase64 = await Promise.all(data.customerImages.map(img => blobUrlToBase64(img.url)));
  const engravingImageBase64 = data.engravingImage?.url ? await blobUrlToBase64(data.engravingImage.url) : '';

  return {
    style_no: data.styleNo,
    jewelry_type: data.jewelryType,
    customer_name: data.customerName,
    customer_design_no: data.customerDesignNo,
    metals: data.metals.map(({ karat, color, netWeight }) => ({ karat, color, netWeight })),
    net_weight: data.netWeight,
    gross_weight: data.grossWeight,
    gross_weight_manual: data.grossWeightManual,
    size: data.size,
    date: data.date,
    designer_name: data.designerName,
    stones: data.stones.map(({ category, stone_category, ownership, stone_shape, stone_quality, setting_type, size_mm, weight_per_stone, quantity, total_weight, type, cut, quality, weight, size }) => ({
      category: category || 'Accent Stone',
      stone_category: stone_category || 'Natural',
      ownership: ownership || 'KS Goods',
      stone_shape: stone_shape || type || 'Round',
      stone_quality: stone_quality || quality || '',
      setting_type: setting_type || 'Prong',
      size_mm: size_mm || size || '',
      weight_per_stone: weight_per_stone || weight || '',
      quantity: quantity || '',
      total_weight: total_weight || 0,
    })),
    findings: data.findings.map(({ type, custom_name, weight, details }) => ({ type, custom_name: custom_name || '', weight, details })),
    other_details: data.otherDetails,
    images,
    finishing: data.finishing || '',
    engraving_instructions: data.engravingInstructions || '',
    engraving_image_base64: engravingImageBase64,
    customer_images_base64: customerImagesBase64,
  };
}

/** Deserialize a stored sheet back to CADSheetData */
export function deserializeSheet(stored) {
  return {
    styleNo: stored.style_no || stored.styleNo || '',
    jewelryType: stored.jewelry_type || stored.jewelryType || '',
    customerName: stored.customer_name || stored.customerName || '',
    customerDesignNo: stored.customer_design_no || stored.customerDesignNo || '',
    metals: (stored.metals || []).map(m => ({ ...m, id: crypto.randomUUID(), netWeight: m.netWeight || m.net_weight || '' })),
    netWeight: stored.net_weight || stored.netWeight || '',
    grossWeight: stored.gross_weight || stored.grossWeight || '',
    grossWeightManual: stored.gross_weight_manual || stored.grossWeightManual || false,
    size: stored.size || '',
    date: stored.date || '',
    designerName: stored.designer_name || stored.designerName || '',
    stones: (stored.stones || []).map(s => ({
      ...s,
      category: s.category || 'Accent Stone',
      stone_category: s.stone_category || 'Natural',
      ownership: s.ownership || 'KS Goods',
      stone_shape: s.stone_shape || s.type || 'Round',
      stone_quality: s.stone_quality || s.quality || '',
      setting_type: s.setting_type || 'Prong',
      size_mm: s.size_mm || s.size || '',
      weight_per_stone: s.weight_per_stone || s.weight || '',
      quantity: s.quantity || '',
      total_weight: s.total_weight || 0,
      id: crypto.randomUUID(),
    })),
    findings: (stored.findings || []).map(f => ({ ...f, custom_name: f.custom_name || '', id: crypto.randomUUID() })),
    otherDetails: stored.other_details || stored.otherDetails || '',
    images: (stored.images || []).map((img, i) => ({
      id: i,
      file: null,
      url: img.base64 || img.url || '',
      label: img.label,
      annotations: (img.annotations || []).map(a => ({ ...a, id: a.id || crypto.randomUUID() })),
      textBoxes: (img.textBoxes || []).map(t => ({ ...t, id: t.id || crypto.randomUUID() })),
      highlights: (img.highlights || []).map(h => ({ ...h, id: h.id || crypto.randomUUID() })),
    })),
    finishing: stored.finishing || '',
    engravingInstructions: stored.engraving_instructions || stored.engravingInstructions || '',
    engravingImage: (stored.engraving_image_base64 || stored.engravingImageBase64) ? {
      id: 0, file: null, url: stored.engraving_image_base64 || stored.engravingImageBase64, label: 'Engraving Reference', annotations: [], textBoxes: [], highlights: [],
    } : null,
    customerImages: ((stored.customer_images_base64 || stored.customerImagesBase64) || []).map((b64, i) => ({
      id: i, file: null, url: b64, label: 'Customer Reference', annotations: [], textBoxes: [], highlights: [],
    })),
  };
}

// ========== MongoDB-backed operations ==========

export async function storeSheet(data, existingId) {
  const payload = await serializeSheet(data);
  try {
    if (existingId) {
      const res = await updateCadSheet(existingId, payload);
      return { success: true, id: res.data.id };
    } else {
      const res = await createCadSheet(payload);
      return { success: true, id: res.data.id };
    }
  } catch (error) {
    console.error('Failed to store sheet:', error);
    return { success: false, error: error.message };
  }
}

export async function listSheets(search = '') {
  try {
    const params = { limit: 200 };
    if (search) params.search = search;
    const res = await listCadSheets(params);
    return (res.data?.sheets || []).map(s => ({
      ...s,
      // Map DB fields back to UI fields for compatibility
      styleNo: s.style_no || s.styleNo || '',
      jewelryType: s.jewelry_type || s.jewelryType || '',
      customerName: s.customer_name || s.customerName || '',
      customerDesignNo: s.customer_design_no || s.customerDesignNo || '',
      designerName: s.designer_name || s.designerName || '',
      savedAt: s.updated_at || s.created_at,
    }));
  } catch (error) {
    console.error('Failed to list sheets:', error);
    return [];
  }
}

export async function getSheet(id) {
  try {
    const res = await getCadSheet(id);
    return res.data || null;
  } catch (error) {
    console.error('Failed to get sheet:', error);
    return null;
  }
}

export async function deleteSheet(id) {
  try {
    await deleteCadSheet(id);
    return true;
  } catch (error) {
    console.error('Failed to delete sheet:', error);
    return false;
  }
}

export async function bulkDeleteSheets(ids) {
  try {
    await bulkDeleteCadSheets(ids);
    return true;
  } catch (error) {
    console.error('Failed to bulk delete sheets:', error);
    return false;
  }
}
